import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Leaf, Recycle, ShieldCheck, DollarSign, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CONDITIONS = [
  { id: "excellent", label: "Excellent — no scratches" },
  { id: "good", label: "Good — light wear" },
  { id: "fair", label: "Fair — visible wear" },
  { id: "damaged", label: "Damaged — cracked / not booting" },
];

export default function BuybackDialog({ open, onOpenChange, device }) {
  const [condition, setCondition] = useState("good");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (c = condition) => {
    if (!device) return;
    setLoading(true);
    try {
      const q = await endpoints.buybackQuote({
        device_name: device.device_name,
        brand: device.brand || "Apple",
        category: device.category || "laptop",
        age_months: device.age_months || 24,
        condition: c,
      });
      setQuote(q);
    } catch { toast.error("Could not fetch quote"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && device) { setCondition("good"); load("good"); }
    if (!open) setQuote(null);
  }, [open, device]);

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="buyback-dialog" className="max-w-lg bg-white">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Recycle size={12} className="text-green-600" /> Sustainable Disposal · Buyback
          </div>
          <DialogTitle className="font-display text-2xl text-slate-900">{device.device_name}</DialogTitle>
          <DialogDescription><span className="font-mono">{device.asset_tag}</span> · Age {device.age_months}mo · {device.brand}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Device Condition</div>
            <Select value={condition} onValueChange={(v) => { setCondition(v); load(v); }}>
              <SelectTrigger data-testid="buyback-condition" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`buyback-cond-${c.id}`}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading && <div className="text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" /> Calculating quote…</div>}

          {quote && !loading && (
            <div className="space-y-3" data-testid="buyback-quote-panel">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-slate-200 rounded-md p-4 bg-gradient-to-br from-green-50 to-white">
                  <div className="flex items-center justify-between text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
                    <span>Estimated Value</span><DollarSign size={14} className="text-green-600" />
                  </div>
                  <div className="mt-1 font-display font-black text-3xl text-slate-900">${quote.estimated_value_usd.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">Credited to your cost center</div>
                </div>
                <div className="border border-slate-200 rounded-md p-4 bg-gradient-to-br from-emerald-50 to-white">
                  <div className="flex items-center justify-between text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
                    <span>CO₂ Saved</span><Leaf size={14} className="text-green-600" />
                  </div>
                  <div className="mt-1 font-display font-black text-3xl text-slate-900">{quote.co2_saved_kg}<span className="text-sm font-normal text-slate-500 ml-1">kg</span></div>
                  <div className="text-xs text-slate-500">vs manufacturing new · {quote.landfill_diverted_kg}kg landfill diverted</div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-md p-4 bg-white">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Disposal Partner</div>
                <div className="font-semibold text-slate-900 mt-0.5">{quote.disposal_partner}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {quote.certifications.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                      <ShieldCheck size={10} /> {c}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Certified Workflow</div>
                <ol className="mt-1 space-y-1">
                  {quote.workflow.map((s, i) => (
                    <li key={s} className="flex items-center gap-2 text-xs text-slate-700">
                      <span className="w-4 h-4 rounded-full bg-[#0033A0] text-white text-[10px] font-bold grid place-items-center">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="buyback-close">Close</Button>
          <Button data-testid="buyback-schedule" onClick={() => { toast.success("Return kit scheduled — ticket auto-created"); onOpenChange(false); }} className="bg-green-600 hover:bg-green-700 text-white">
            Schedule Return Kit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
